import { useMemo } from 'react';
import { Departamento } from '../api/Catalogos/types/CAT-012.types';
import { MunicipioItem } from '../api/Catalogos/types/CAT-013.types';

interface LocationResolver {
  getDepartamentoCodigoById: (id: string | number) => string | null;
  getMunicipioCodigoById: (id: string | number) => string | null;
  getDepartamentoNombreById: (id: string | number) => string | null;
  getMunicipioNombreById: (id: string | number) => string | null;
  getDistritoNombreById: (id: string | number, municipioId?: string | number) => string | null;
}

/**
 * Hook para resolver nombres y códigos de ubicaciones geográficas desde IDs
 * Útil para convertir IDs de paciente a códigos requeridos para facturas
 */
export const useLocationResolver = (
  departamentos: Departamento[],
  municipios: MunicipioItem[]
): LocationResolver => {
  
  const resolver = useMemo((): LocationResolver => {
    const getDepartamentoCodigoById = (id: string | number): string | null => {
      const numericId = Number(id);
      if (isNaN(numericId)) return null;
      
      const departamento = departamentos.find(dept => dept.id === numericId);
      return departamento ? departamento.codigo : null;
    };

    const getMunicipioCodigoById = (id: string | number): string | null => {
      const numericId = Number(id);
      if (isNaN(numericId)) return null;
      
      const municipio = municipios.find(mun => mun.id === numericId);
      return municipio ? municipio.codigo : null;
    };

    const getDepartamentoNombreById = (id: string | number): string | null => {
      const numericId = Number(id);
      if (isNaN(numericId)) return null;
      
      const departamento = departamentos.find(dept => dept.id === numericId);
      return departamento ? departamento.nombre : null;
    };

    const getMunicipioNombreById = (id: string | number): string | null => {
      const numericId = Number(id);
      if (isNaN(numericId)) return null;
      
      const municipio = municipios.find(mun => mun.id === numericId);
      return municipio ? municipio.nombre : null;
    };

    const getDistritoNombreById = (id: string | number, municipioId?: string | number): string | null => {
      const numericId = Number(id);
      if (isNaN(numericId)) return null;
      
      // Si tenemos el municipio ID, buscar específicamente en ese municipio
      if (municipioId) {
        const numericMunicipioId = Number(municipioId);
        const municipio = municipios.find(mun => mun.id === numericMunicipioId);
        if (municipio) {
          const distrito = municipio.distritos.find(dist => dist.id === numericId);
          return distrito ? distrito.nombre : null;
        }
      }
      
      // Buscar en todos los municipios
      for (const municipio of municipios) {
        const distrito = municipio.distritos.find(dist => dist.id === numericId);
        if (distrito) {
          return distrito.nombre;
        }
      }
      
      // Buscar en departamentos como fallback
      for (const departamento of departamentos) {
        for (const municipio of departamento.municipios) {
          const distrito = municipio.distritos.find(dist => dist.id === numericId);
          if (distrito) {
            return distrito.nombre;
          }
        }
      }
      
      return null;
    };

    return {
      getDepartamentoCodigoById,
      getMunicipioCodigoById,
      getDepartamentoNombreById,
      getMunicipioNombreById,
      getDistritoNombreById
    };
  }, [departamentos, municipios]);

  return resolver;
};

/**
 * Función utilitaria para resolver códigos de departamento y municipio desde IDs
 * Útil para servicios que no pueden usar hooks
 */
export const resolveLocationCodes = (
  departamentoId: string | number | undefined,
  municipioId: string | number | undefined,
  departamentos: Departamento[],
  municipios: MunicipioItem[]
): { departamentoCodigo: string; municipioCodigo: string } => {
  let departamentoCodigo = '06'; // San Salvador por defecto
  let municipioCodigo = '14'; // San Salvador por defecto
  
  if (departamentoId) {
    const numericDeptId = Number(departamentoId);
    if (!isNaN(numericDeptId)) {
      const dept = departamentos.find(d => d.id === numericDeptId);
      if (dept) {
        departamentoCodigo = dept.codigo;
      }
    }
  }
  
  if (municipioId) {
    const numericMunId = Number(municipioId);
    if (!isNaN(numericMunId)) {
      const mun = municipios.find(m => m.id === numericMunId);
      if (mun) {
        municipioCodigo = mun.codigo;
      }
    }
  }
  
  return { departamentoCodigo, municipioCodigo };
};
